import React, { useState } from "react";
import { firstCompany } from "../../../helper/attribute";
import { updateCompany } from "../../../firebase/company/update";
import { Select, Table, Typography, Alert, message, Modal } from 'antd';

const { Title } = Typography;

export const checkLocks = (result, company) => {
  return new Promise((resolve, reject) => {
    // Extract year from DD/MM/YYYY format
    const year = parseInt(result.date.split('/')[2]);
    const currentYear = new Date().getFullYear();

    // Skip check for current year
    if (year === currentYear) {
      resolve(true);
      return;
    }

    // Check global lock first
    const globalLock = company.previous_years_lock || 'none';
    if (globalLock !== 'none') {
      if (globalLock === 'hard') {
        Modal.error({
          title: 'Access Denied',
          content: `Cannot modify data from ${year}. Hard lock is enabled for all previous years.`,
          onOk: () => reject(new Error('Hard lock enabled'))
        });
        return;
      }
      if (globalLock === 'soft') {
        Modal.confirm({
          title: 'Warning',
          content: `You are modifying data from ${year}. Soft lock is enabled for all previous years.`,
          onOk: () => resolve(true),
          onCancel: () => reject(new Error('User cancelled')),
          okText: 'Proceed',
          cancelText: 'Cancel'
        });
        return;
      }
    }

    // If no global lock, check year-specific locks
    try {
      const yearLocks = JSON.parse(company.year_locks || '{}');
      const yearLock = yearLocks[year] || 'none';

      if (yearLock === 'hard') {
        Modal.error({
          title: 'Access Denied',
          content: `Cannot modify data from ${year}. Hard lock is enabled for this year.`,
          onOk: () => reject(new Error('Hard lock enabled'))
        });
        return;
      }
      if (yearLock === 'soft') {
        Modal.confirm({
          title: 'Warning',
          content: `You are modifying data from ${year}. Soft lock is enabled for this year.`,
          onOk: () => resolve(true),
          onCancel: () => reject(new Error('User cancelled')),
          okText: 'Proceed',
          cancelText: 'Cancel'
        });
        return;
      }
    } catch (error) {
      console.error('Error parsing year locks:', error);
      reject(error);
      return;
    }

    resolve(true);
  });
};

const LockData = () => {
  const [company] = useState(firstCompany());
  const [globalLock, setGlobalLock] = useState(company.previous_years_lock || 'none');
  const [yearLocks, setYearLocks] = useState(() => {
    try {
      return JSON.parse(company.year_locks || '{}');
    } catch {
      return {};
    }
  });

  const handleGlobalLockChange = async (value) => {
    setGlobalLock(value);
    await updateCompany(company.id, { previous_years_lock: value });
  };

  const handleYearLockChange = async (year, value) => {
    const newYearLocks = { ...yearLocks, [year]: value };
    setYearLocks(newYearLocks);
    await updateCompany(company.id, { year_locks: JSON.stringify(newYearLocks) });
  };

  // Generate years from current year-1 to 2019
  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - 2018 }, 
    (_, i) => currentYear - i - 1
  ).filter(year => year >= 2019);

  const columns = [
    {
      title: 'Year',
      dataIndex: 'year',
      key: 'year',
    },
    {
      title: 'Lock Type',
      dataIndex: 'lock',
      key: 'lock',
      render: (_, record) => (
        <Select
          value={yearLocks[record.year] || 'none'}
          onChange={(value) => handleYearLockChange(record.year, value)}
          style={{ width: '100%' }}
          options={[
            { value: 'none', label: 'No Lock' },
            { value: 'soft', label: 'Soft Lock' },
            { value: 'hard', label: 'Hard Lock' },
          ]}
        />
      ),
    },
  ];

  const tableData = years.map(year => ({
    key: year,
    year: year,
  }));

  return (
    <div className="p-4">
      <div className="mb-6">
        <Title level={2}>Lock Data for {company.title}</Title>
        
        <Alert
          className="mb-6"
          message="Lock Settings Information"
          description={
            <ul>
              <li>These lock settings do not affect the current year's data.</li>
              <li>When Global Lock is set, users cannot add or modify data for any year before the current year.</li>
              <li>Year-specific locks become available when Global Lock is set to "No Lock".</li>
            </ul>
          }
          type="info"
          showIcon
        />

        <div className="mb-6">
          <Title level={4}>Global Lock Setting</Title>
          <Select
            value={globalLock}
            onChange={handleGlobalLockChange}
            style={{ width: 200 }}
            options={[
              { value: 'none', label: 'No Lock' },
              { value: 'soft', label: 'Soft Lock' },
              { value: 'hard', label: 'Hard Lock' },
            ]}
          />
        </div>

        {globalLock === 'none' && (
          <div>
            <Title level={4}>Year-specific Locks</Title>
            <Table 
              columns={columns} 
              dataSource={tableData}
              pagination={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default LockData;
